import { http } from "./http";

export type SessionKind = "None" | "User" | "Customer" | "Agent" | "Manager" | "Admin";

export interface SessionDto {
  isAuthenticated: boolean;
  kind: SessionKind;
  displayName?: string | null;
  userAuthId?: string | null;
  userKey?: string | null;
  userId?: number;
  customerId?: number;
  // Set for manager sessions; drives "you see your own customers" copy.
  // Server-side scoping never trusts this value — it reads the session.
  managerId?: number;
  portalSessionKey?: string | null;
  checkSiblings?: boolean;
  mustChangePassword?: boolean;
  redirectToManagerId?: number;
  validationKey?: string | null;
  // Impersonation context: populated when an admin is currently
  // signed in as a customer via /api/admin/customers/{id}/switch.
  // The SPA shows a compact banner (admin name + switch back) while
  // these are set.
  originalKind?: SessionKind | null;
  originalUserAuthId?: string | null;
  originalDisplayName?: string | null;
  /**
   * Permission tokens copied from the API session (ServiceStack's
   * `Permissions` list) so widgets can be gated client-side using
   * the same `CUSTOMER.DASHBOARD.WIDGETS.*` strings the legacy
   * Razor portal used.
   */
  permissions?: string[] | null;
  /**
   * Configured per-login restriction (API appsettings "RestrictedLogins").
   * "RecordingsSearchOnly": only the Recordings report, search by call id.
   */
  restriction?: string | null;
  /** Customer report time zone as stored (sys.time_zone_info name); null = UTC. */
  timeZone?: string | null;
  /** IANA form used for formatting in the browser; "UTC" when unset. */
  timeZoneIana?: string | null;
}

/** Route a restricted session is confined to, or null when unrestricted. */
export function restrictedHome(session: SessionDto | null | undefined): string | null {
  return session?.restriction === "RecordingsSearchOnly" ? "/reports/recordings" : null;
}

/**
 * Route prefixes a menu-restricted session may open, or null when the whole
 * customer menu is available. "BasicMenu": Dashboard, Numbers, Voice, Data
 * sheets and Reports (the API blocks the other features too).
 */
export function restrictedMenuPrefixes(session: SessionDto | null | undefined): string[] | null {
  return session?.restriction === "BasicMenu" ? ["/dashboard", "/numbers", "/voice", "/data", "/reports"] : null;
}

/** True when the route is reachable under the session's menu restriction (unrestricted → always). */
export function isRouteAllowed(session: SessionDto | null | undefined, pathname: string): boolean {
  const prefixes = restrictedMenuPrefixes(session);
  if (!prefixes) return true;
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export interface LoginRequest {
  username: string;
  password: string;
  customerId?: string | null;
  rememberMe: boolean;
}

export interface LoginResponse {
  ok: boolean;
  error?: string | null;
  session?: SessionDto | null;
}

export interface ResetKeyInfo {
  valid: boolean;
  username?: string | null;
  portalName?: string | null;
}

export const authApi = {
  /** Self-service: always resolves ok, whether or not the account exists. */
  forgotPassword: (username: string) =>
    http<{ ok: boolean }>("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ username }) }),
  validateResetKey: (key: string) => http<ResetKeyInfo>(`/api/auth/reset-password/${encodeURIComponent(key)}`),
  resetPassword: (key: string, password: string) =>
    http<{ ok: boolean }>("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ key, password }) }),

  login: (request: LoginRequest) =>
    http<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(request),
    }),
  logout: () => http<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  /** Signed-in user changes their own password (errors: wrong_password, password_too_short, …). */
  changePassword: (currentPassword: string, newPassword: string) =>
    http<{ ok: boolean }>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  session: () => http<SessionDto>("/api/auth/session"),
};
